import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import type { ActivityItem } from '../../services/activityService';
import { formatDateTime } from '../../dateFormat';

interface Props {
  items: ActivityItem[];
  loading: boolean;
  error: string | null;
}

const RTF = typeof Intl !== 'undefined' && (Intl as any).RelativeTimeFormat
  ? new (Intl as any).RelativeTimeFormat(undefined, { numeric: 'auto' })
  : null;

function formatRelative(timestamp: string): string {
  if (!RTF) return formatDateTime(timestamp);
  const then = new Date(timestamp).getTime();
  const now = Date.now();
  const diffSec = Math.round((then - now) / 1000);
  const absSec = Math.abs(diffSec);
  if (absSec < 60) return RTF.format(diffSec, 'second');
  const diffMin = Math.round(diffSec / 60);
  if (Math.abs(diffMin) < 60) return RTF.format(diffMin, 'minute');
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return RTF.format(diffHr, 'hour');
  const diffDay = Math.round(diffHr / 24);
  return RTF.format(diffDay, 'day');
}

function eventIcon(item: ActivityItem): { icon: string; tone: string } {
  switch (item.type) {
    case 'combo_started':            return { icon: 'bi-play-circle',            tone: 'tone-combo' };
    case 'combo_completed':          return { icon: 'bi-check-circle',           tone: 'tone-combo' };
    case 'combo_marked_incomplete':  return { icon: 'bi-dash-circle',            tone: 'tone-combo' };
    case 'combo_cancelled':          return { icon: 'bi-x-circle',               tone: 'tone-combo' };
    case 'session_signed_off':       return { icon: 'bi-patch-check',            tone: 'tone-session' };
    case 'session_signoff_revoked':  return { icon: 'bi-arrow-counterclockwise', tone: 'tone-session' };
    case 'session_marked_incomplete':
    case 'session_incomplete':       return { icon: 'bi-exclamation-circle',     tone: 'tone-session' };
    case 'session_cancelled':        return { icon: 'bi-x-circle',               tone: 'tone-session' };
    case 'session_completed':        return { icon: 'bi-check2-circle',          tone: 'tone-session' };
    case 'session_deleted':          return { icon: 'bi-trash',                  tone: 'tone-session' };
    default:                         return { icon: item.module === 'spraying' ? 'bi-droplet' : 'bi-bug', tone: '' };
  }
}

export const ActivityFeed: React.FC<Props> = ({ items, loading, error }) => {
  const { t } = useTranslation();

  return (
    <section className="activity-feed">
      <h3 className="activity-feed__title">{t('home.activity.title')}</h3>

      {loading && (
        <div role="status" className="activity-feed__loading">{t('common.loading')}</div>
      )}
      {error && (
        <div role="alert" className="activity-feed__error">{t('home.activity.error')}</div>
      )}
      {!loading && !error && items.length === 0 && (
        <div className="activity-feed__empty">{t('home.activity.empty')}</div>
      )}

      {!loading && !error && items.length > 0 && (
        <ul className="activity-feed__list">
          {items.map(item => {
            const { icon, tone } = eventIcon(item);
            return (
              <li key={item.id} className={tone ? `activity-feed__item ${tone}` : 'activity-feed__item'}>
                <Link to={item.deepLink} className="activity-feed__link">
                  <i className={`bi ${icon} activity-feed__icon`} aria-hidden="true" />
                  <span className="activity-feed__actor">{item.actor}</span>
                  <span className="activity-feed__verb"> {t(item.verb)} </span>
                  <span className="activity-feed__object">{item.object}</span>
                  <span className="activity-feed__time"> · {formatRelative(item.timestamp)}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default ActivityFeed;
